import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';

import {
  AccountForSearchSubscriptionFeaturesQuery,
  SearchSubscriptionFeaturesRequirementsQuery,
} from './graphql/queries';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../../temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import { useLocalFeatures } from './helpers';

/**
 * Determines whether a feature is available the current
 * logged in account or not.
 *
 * Do not use as is.
 *
 * @see useFeaturePermission
 *
 * @param {Object} options The option list.
 * @param {Array<String>} options.features List of IDs of features to check for
 */
const useSearchSubscriptionFeature = ({ features = [] } = {}) => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);

  const {
    loading: loadingSearchSubscriptionFeaturesRequirements,
    data: { searchSubscriptionFeaturesRequirements } = {},
  } = useQuery(SearchSubscriptionFeaturesRequirementsQuery);
  const {
    loading: loadingSearchSubscriptionItems,
    data: { searchSubscriptionItems } = {},
  } = useQuery(getPhotoSearchCheckoutSearchItemsQuery);

  const { loading: loadingAccount, data: { me: account } = {} } = useQuery(
    AccountForSearchSubscriptionFeaturesQuery,
    {
      skip: !authenticated,
      notifyOnNetworkStatusChange: true,
    },
  );

  const loading =
    loadingAccount ||
    loadingSearchSubscriptionItems ||
    loadingSearchSubscriptionFeaturesRequirements;

  if (
    // Un-authed users should never land here - unless we explicitly lock contributor/public
    // features behind a paywall.
    !authenticated ||
    // When loading, return nothing enabled just yet.
    loading
  ) {
    return features.reduce(
      (acc, featureId) => {
        return {
          ...acc,
          [featureId]: {
            enabled: false,
            plans: searchSubscriptionFeaturesRequirements?.[featureId],
          },
        };
      },
      { id: null, loading },
    );
  }

  const localFeatures = useLocalFeatures({ account, searchSubscriptionItems });

  return features.reduce(
    (acc, featureId) => {
      return {
        ...acc,
        [featureId]:
          typeof account?.active_search_subscription_features?.[featureId] ===
          'undefined'
            ? // If not defined within the account's search subscription features object
              // we will fall-back to a local feature instead.
              localFeatures[featureId] ?? { enabled: false, plans: [] }
            : {
                enabled: !!account?.active_search_subscription_features?.[
                  featureId
                ],
                plans: searchSubscriptionFeaturesRequirements?.[featureId],
              },
      };
    },
    { id: account?.id },
  );
};

export default useSearchSubscriptionFeature;
